/**
 * ClerkGate — Conditional ClerkProvider + TokenBridge wrapper (Steps 3–4).
 *
 * When REACT_APP_USE_CLERK_AUTH=false (default):
 *   → Pure passthrough. No Clerk code loaded. No network calls.
 *
 * When REACT_APP_USE_CLERK_AUTH=true AND REACT_APP_CLERK_PUBLISHABLE_KEY set:
 *   → Lazily loads ClerkProvider from @clerk/clerk-react.
 *   → Wraps children with ClerkProvider.
 *   → Step 4: Also wraps with ClerkTokenBridge (injects getToken into authHeaders).
 *
 * Uses React.lazy to avoid bundling Clerk in the main chunk when disabled.
 *
 * ⚠️ Suspense fallback is `null` (blank screen during chunk load) to avoid
 *   rendering <App/> once outside ClerkProvider and then remounting inside it.
 *   The lazy chunk is small (~50-100ms on fast connections).
 *
 * See: docs/architecture/shared-auth-migration-master-plan.md (Steps 3–4)
 */
import React from 'react';

const USE_CLERK = process.env.REACT_APP_USE_CLERK_AUTH === 'true';
const CLERK_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

// Only define the lazy components when Clerk is enabled.
// This ensures @clerk/clerk-react is never imported when disabled.
const LazyClerkWrapper = USE_CLERK && CLERK_KEY
  ? React.lazy(() => import('./ClerkWrapper'))
  : null;

// Step 4: Token bridge also lazy-loaded (depends on @clerk/clerk-react hooks)
const LazyClerkTokenBridge = USE_CLERK && CLERK_KEY
  ? React.lazy(() => import('./ClerkTokenBridge'))
  : null;

export default function ClerkGate({ children }) {
  // Default: pure passthrough (no Clerk)
  if (!LazyClerkWrapper) {
    return <>{children}</>;
  }

  // Clerk enabled: wrap with lazy-loaded ClerkProvider + TokenBridge.
  // Suspense fallback is null — NOT {children} — to avoid rendering App
  // outside ClerkProvider and then remounting it inside (double-mount).
  return (
    <React.Suspense fallback={null}>
      <LazyClerkWrapper>
        <LazyClerkTokenBridge>
          {children}
        </LazyClerkTokenBridge>
      </LazyClerkWrapper>
    </React.Suspense>
  );
}
